import { useState, useEffect, useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { TastingExperienceMediaUpload } from './TastingExperienceMediaUpload';
import { WineSelector } from './WineSelector';
import { FormSection } from './ui/FormSection';
import { ExperienceBasicInfoFields } from './TastingExperienceForm/ExperienceBasicInfoFields';
import { ExperiencePricingFields } from './TastingExperienceForm/ExperiencePricingFields';
import { ExperienceRequirementsFields } from './TastingExperienceForm/ExperienceRequirementsFields';
import type { TastingExperienceStatus } from '@wineryxplorer/shared';
import type { TastingExperienceFormData } from '../types/tastingExperience';
import type { TastingExperienceFormErrors } from './TastingExperienceForm/types';

export interface TastingExperienceFormProps {
  initialData?: Partial<TastingExperienceFormData>;
  onSubmit: (data: TastingExperienceFormData) => Promise<void>;
  onCancel: () => void;
  venueId?: string;
  isSubmitting?: boolean;
  hideButtons?: boolean;
  submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

export const TastingExperienceForm = ({
  initialData,
  onSubmit,
  onCancel,
  venueId: propVenueId,
  isSubmitting = false,
  hideButtons = false,
  submitRef,
}: TastingExperienceFormProps) => {
  // Form state
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [venueId, setVenueId] = useState(initialData?.venueId || propVenueId || '');
  const [priceAmount, setPriceAmount] = useState(initialData?.price?.amount.toString() || '');
  const [duration, setDuration] = useState(initialData?.duration?.toString() || '');
  const [wines, setWines] = useState<string[]>(initialData?.wines || []);
  const [requiresReservation, setRequiresReservation] = useState(
    initialData?.requiresReservation ?? true
  );
  const [availability, setAvailability] = useState(initialData?.availability || '');
  const [heroImageId, setHeroImageId] = useState(initialData?.heroImageId || '');
  const [imageIds, setImageIds] = useState<string[]>(initialData?.imageIds || []);
  const [videoIds, setVideoIds] = useState<string[]>(initialData?.videoIds || []);
  const [tags, setTags] = useState(initialData?.tags?.join(', ') || '');
  const [wineClubOnly, setWineClubOnly] = useState(initialData?.wineClubOnly ?? false);
  const [status, setStatus] = useState<TastingExperienceStatus>(initialData?.status || 'draft');

  // Validation errors
  const [errors, setErrors] = useState<TastingExperienceFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Fetch venues for selection if no venueId provided
  const showVenueSelect = !propVenueId && !initialData?.venueId;
  const { data: venuesData } = trpc.venue.list.useQuery({ limit: 100 }, { enabled: showVenueSelect });
  const venueOptions =
    venuesData?.items.map((venue) => ({ id: venue._id.toString(), name: venue.name })) ?? [];

  // Update venueId when prop changes
  useEffect(() => {
    if (propVenueId && !venueId) {
      setVenueId(propVenueId);
    }
  }, [propVenueId, venueId]);

  const validateForm = useCallback((): boolean => {
    const newErrors: TastingExperienceFormErrors = {};

    // Required fields
    if (!name.trim()) {
      newErrors.name = 'Experience name is required';
    } else if (name.length > 200) {
      newErrors.name = 'Experience name must be 200 characters or less';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!venueId) {
      newErrors.venueId = 'Venue is required';
    }

    // Validate price
    if (!priceAmount) {
      newErrors.price = 'Price is required';
    } else {
      const priceNum = parseFloat(priceAmount);
      if (isNaN(priceNum) || priceNum < 0) {
        newErrors.price = 'Price must be a non-negative number';
      }
    }

    // Validate duration if provided
    if (duration) {
      const durationNum = parseInt(duration, 10);
      if (isNaN(durationNum) || durationNum < 1) {
        newErrors.duration = 'Duration must be at least 1 minute';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, description, venueId, priceAmount, duration]);

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSubmit = useCallback(async () => {
    // Mark all fields as touched
    setTouched({
      name: true,
      description: true,
      venueId: true,
      price: true,
      duration: true,
    });

    if (!validateForm()) {
      return;
    }

    const formData: TastingExperienceFormData = {
      name: name.trim(),
      description: description.trim(),
      venueId,
      price: {
        amount: parseFloat(priceAmount),
        currency: 'USD',
      },
      duration: duration ? parseInt(duration, 10) : undefined,
      wines: wines.length > 0 ? wines : undefined,
      requiresReservation,
      availability: availability.trim() || undefined,
      heroImageId: heroImageId || undefined,
      imageIds,
      videoIds,
      tags: tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
      wineClubOnly,
      status,
    };

    await onSubmit(formData);
  }, [
    name,
    description,
    venueId,
    priceAmount,
    duration,
    wines,
    requiresReservation,
    availability,
    heroImageId,
    imageIds,
    videoIds,
    tags,
    wineClubOnly,
    status,
    onSubmit,
    validateForm,
  ]);

  // Expose submit handler to parent via ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
  }, [submitRef, handleSubmit]);

  return (
    <div>
      <ExperienceBasicInfoFields
        showVenueSelect={showVenueSelect}
        venueOptions={venueOptions}
        venueId={venueId}
        onVenueIdChange={setVenueId}
        name={name}
        onNameChange={setName}
        description={description}
        onDescriptionChange={setDescription}
        status={status}
        onStatusChange={setStatus}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <ExperiencePricingFields
        priceAmount={priceAmount}
        onPriceAmountChange={setPriceAmount}
        duration={duration}
        onDurationChange={setDuration}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        isSubmitting={isSubmitting}
      />

      <ExperienceRequirementsFields
        requiresReservation={requiresReservation}
        onRequiresReservationChange={setRequiresReservation}
        wineClubOnly={wineClubOnly}
        onWineClubOnlyChange={setWineClubOnly}
        availability={availability}
        onAvailabilityChange={setAvailability}
        isSubmitting={isSubmitting}
      />

      {/* Wine Selection */}
      {venueId && (
        <FormSection
          title="Wines Included"
          description="Choose which of this venue's wines are poured during the experience."
        >
          <WineSelector
            venueId={venueId}
            selectedWineIds={wines}
            onSelectionChange={setWines}
            disabled={isSubmitting}
          />
        </FormSection>
      )}

      {/* Tags */}
      <FormSection title="Tags" description="Separate tags with commas.">
        <label htmlFor="tags" className="sr-only">
          Tags
        </label>
        <input
          id="tags"
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          disabled={isSubmitting}
          placeholder="wine, tasting, premium (comma-separated)"
          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
            isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        />
      </FormSection>

      {/* Media Upload */}
      {venueId && (
        <TastingExperienceMediaUpload
          venueId={venueId}
          heroImageId={heroImageId}
          imageIds={imageIds}
          videoIds={videoIds}
          onHeroImageChange={setHeroImageId}
          onImagesChange={setImageIds}
          onVideosChange={setVideoIds}
        />
      )}

      {/* Form Actions */}
      {!hideButtons && (
        <div className="mt-6 flex gap-4 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex-1 px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Save Experience'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={`px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
